import {
  DayVolume, SCHEDULE_DAYS, ScheduleDay, ScheduleDriver, ScheduleWorkArea, WeekDay, WeekDriver, driverOn,
} from '../../api/services/schedule.service';

const DAY_MS = 86400000;

export const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** The Friday that ends the FedEx week holding this local calendar day. */
export function weekEndingFor(date: Date): string {
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  return isoDay(new Date(utc.getTime() + ((5 - utc.getUTCDay() + 7) % 7) * DAY_MS));
}

export const shiftWeek = (weekEnding: string, weeks: number) => isoDay(
  new Date(new Date(`${weekEnding}T00:00:00Z`).getTime() + weeks * 7 * DAY_MS),
);

export const toggleDay = (days: ScheduleDay[], day: ScheduleDay) => (
  days.includes(day) ? days.filter((d) => d !== day) : [...days, day]
);

export interface DayRoom {
  stops: number;
  packages: number;
  /** Still under capacity once the day's pickups are counted too? */
  stopsWithPickups: number;
  packagesWithPickups: number;
}

/** Capacity left after the day's volume; negative means understaffed. */
export function roomLeft(day: Pick<WeekDay, 'maxStops' | 'maxPackages'>, volume: DayVolume): DayRoom {
  const stops = day.maxStops - volume.stops;
  const packages = day.maxPackages - volume.packages;
  return {
    stops,
    packages,
    stopsWithPickups: stops - volume.pickupStops,
    packagesWithPickups: packages - volume.pickupPackages,
  };
}

/** The changes to send: every driver on the week, the server keeps the differences. */
export const driverChanges = (drivers: WeekDriver[]) => drivers.map((d) => ({ accountId: d.accountId, days: d.days }));

export type DayVerdict = 'short' | 'tight' | 'ok' | 'unknown';

/** Short: over capacity. Tight: fits only if pickups do not count against it. */
export function verdict(day: WeekDay): DayVerdict {
  if (!day.volume || day.closed) return 'unknown';
  const room = roomLeft(day, day.volume);
  if (room.stops < 0 || room.packages < 0) return 'short';
  if (room.stopsWithPickups < 0 || room.packagesWithPickups < 0) return 'tight';
  return 'ok';
}

// ---------------------------------------------------------------- work areas

/** "Jonathan Ngobeh" → "Jonathan N." */
export const shortName = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts.length < 2 ? name : `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

/**
 * Give a WA's day to someone ('usual' puts it back to the usual driver;
 * null: nobody, a floater covers). The day becomes a dispatched day.
 */
export function setDayDriver(w: ScheduleWorkArea, day: ScheduleDay, who: string | null | 'usual'): ScheduleWorkArea {
  const days = w.days.includes(day) ? w.days : SCHEDULE_DAYS.filter((d) => d === day || w.days.includes(d));
  const others = (w.dayDrivers || []).filter((x) => x.day !== day);
  const keep = who !== 'usual' && who !== (w.driverId || null);
  return { ...w, days, dayDrivers: keep ? [...others, { day, accountId: who as string | null }] : others };
}

/** Not dispatched that day: off the plan, and any exception with it. */
export const clearDay = (w: ScheduleWorkArea, day: ScheduleDay): ScheduleWorkArea => ({
  ...w, days: w.days.filter((d) => d !== day), dayDrivers: (w.dayDrivers || []).filter((x) => x.day !== day),
});

/** A new usual driver; exceptions that now name them are no longer exceptions. */
export const setUsualDriver = (w: ScheduleWorkArea, accountId: string | null): ScheduleWorkArea => ({
  ...w, driverId: accountId, dayDrivers: (w.dayDrivers || []).filter((x) => x.accountId !== accountId),
});

/** The WAs someone runs on a day. */
export const workAreasOf = (workAreas: ScheduleWorkArea[], accountId: string, day: ScheduleDay) => workAreas
  .filter((w) => w.days.includes(day) && driverOn(w, day) === accountId)
  .map((w) => w.number);

/**
 * A saved driver put on a WA for a day they are not scheduled to work gets
 * that day: being assigned a route means working that day. Returns the
 * drivers and what was added, to say so.
 */
export function addAssignedDays(drivers: ScheduleDriver[], workAreas: ScheduleWorkArea[]) {
  const added: { name: string; day: ScheduleDay }[] = [];
  const next = drivers.map((d) => {
    const missing = SCHEDULE_DAYS.filter((day) => !d.days.includes(day) && workAreasOf(workAreas, d.accountId, day).length);
    missing.forEach((day) => added.push({ name: d.name, day }));
    return missing.length ? { ...d, days: SCHEDULE_DAYS.filter((day) => d.days.includes(day) || missing.includes(day)) } : d;
  });
  return { drivers: next, added };
}

/**
 * The worksheet's work areas laid over the saved ones: a WA keeps what was
 * set by hand (its name, driver and exceptions) and takes the worksheet's
 * for whatever is still empty; a WA only on the worksheet is added.
 */
export function mergeWorkAreas(saved: ScheduleWorkArea[], suggested: ScheduleWorkArea[]): ScheduleWorkArea[] {
  const out = saved.map((w) => {
    const s = suggested.find((x) => x.number === w.number);
    if (!s) return w;
    const empty = !w.driverId && !(w.dayDrivers || []).length;
    return {
      ...w,
      name: w.name || s.name || '',
      days: w.days.length ? w.days : s.days,
      ...(empty ? { driverId: s.driverId || null, dayDrivers: s.dayDrivers || [] } : {}),
    };
  });
  for (const s of suggested) if (!out.some((w) => w.number === s.number)) out.push(s);
  return out.sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
}
